import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels} from "@techiediaries/ngx-qrcode";

import { Place } from '@app/modules/model/place';
import { User } from '@app/modules/model/user';
import { QrCode } from '@app/modules/model/qrcode';
import { DrawItem } from '@app/modules/model/draw';
import { Ticket } from '@app/modules/model/ticket';

import { ModalController } from '@ionic/angular';

import { alertService } from '@app/modules/common/alert';
import { languageService, languageSupport } from '@app/modules/common/language';
import { syncService } from '@app/modules/sync';
import { deviceService } from '@app/modules/sockets';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';
import { AppConstants } from '@app/app.constants';

import { UiCashGuardCashPayModal } from '@app/components/model/ticket/ui-cashguard-cash/ui-cashguard-cash';

/************************************/
/* TABLE COMPONENT                  */
/************************************/

@Component({
    selector: 'ui-table-view',
    templateUrl: 'ui-view.html',
    styleUrls: [
      'ui-view.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiQrCodeView extends languageSupport implements OnInit, OnChanges, OnDestroy {    
    @Input('place') _place: Place = null; 
    @Input('user') _user: User = null;
    @Input('table') _table: QrCode = null; 

    private _attention = false;
    get Attention(){
        return this._attention || this._table.Attention;
    }

    get CanModify(){
        return this._place.AllowTableManage(this._user);
    }    

    get HeaderColor(){
        if (this._table.IsError){
            return 'danger'
        }

       return 'warning';
    }

    get ChooseArea(){
        return this.PlaceAreas.length > 0;
    }

    get ChooseTill(){
        return this._place.MultiplePOS;
    }

    private _requests = null;
    get Requests(){
        if (this._requests == null){
            let _requests = [];
            for(let _request of this._table.requests){
                if ((_request.IsValid) && (_request.IsRecent)){
                    _requests.push(_request);
                }
            }

            _requests.sort((a, b) => {
                return b.created.getTime() - a.created.getTime();
            });

            this._requests = _requests;
        }

        return this._requests;
    }

    private _CheckPending(_ticket){
        if (_ticket.IsValid && _ticket.IsRecent && (_ticket.qrcode == this._table)){
            if (!_ticket.IsPaid || (!_ticket.IsDirect && !_ticket.IsReady)){
                return true;
            }
        }
        return false;
    }

    private _pending = null;
    get Pending(){
        if (this._pending == null){
            let _pending = [];
            for(let _ticket of this._place.tickets){
                if (this._CheckPending(_ticket)){
                    _pending.push(_ticket);
                }
            }
            this._pending = _pending;
        }

        return this._pending;
    }

    private _giveChange(request){
        let _ticket = request.ticket;
        if (_ticket && _ticket.given){
            return Number(_ticket.given);
        }
        return 0;
    }

    private _backChange(request){
        let _given = (request.ticket) ? this._giveChange(request) : 0;
        if (_given > 0){
            return Math.round((_given - request.ticket.price) * 100) / 100;
        }
        return 0;
    }

    private _brief = null;
    get Brief(){
        if (this._brief == null){
            let _brief = {
                isgeneric: true,
                refund: 0,              // total amount to be refunded
                torefund: false,        // true if refund is pending
                pending: 0,             // table tickets with payment pending
                paycash: 0,             // total to pay in cash
                givechange: 0,          // amount given
                backchange: 0,          // amount to return
                paycard: 0,             // total to pay in card
                amount: 0               // total amount to pay / refund
            };
            
            for(let _request of this.Requests){
                if (_request.reason != null){
                    _brief.isgeneric = false;
                }

                switch(_request.reason){
                    case 'REFUND':
                        _brief.refund += Number(_request.amount);

                        if (_request.ticket.payment == 'PAYCARD'){
                            _brief.paycard -= Number(_request.amount);
                        }

                        if (_request.ticket.payment == 'PAYCASH'){
                            _brief.paycash -= Number(_request.amount);                            
                        }

                        break;

                    case 'PAYCARD':
                        _brief.pending += Number(_request.ticket.price);
                        _brief.paycard += Number(_request.ticket.price);
                        break;

                    case 'PAYCASH':
                        _brief.pending += Number(_request.ticket.price);
                        _brief.paycash += Number(_request.ticket.price);

                        _brief.givechange += this._giveChange(_request);
                        _brief.backchange += this._backChange(_request);
                        break;
                }
            }
    
            _brief.pending = Math.round(Math.abs(_brief.pending) * 100)/100;
            _brief.givechange = Math.round(Math.abs(_brief.givechange) * 100)/100;
            _brief.backchange = Math.round(Math.abs(_brief.backchange) * 100)/100;
            _brief.amount = _brief.pending - _brief.refund;
            _brief.torefund = _brief.amount < 0;
            _brief.amount = Math.round(Math.abs(_brief.amount) * 100)/100;

            this._brief = _brief;
        }

        return this._brief;
    }

    TicketElapsed(ticket){
        let _min = Math.round(((new Date()).getTime() - ticket.created.getTime()) / 60000);

        if (_min < 1){
            return this.lang.tr('@created_time_just_now');            
        }
        
        if (_min < 60){
            return this.lang.tr('@created_time_minutes', [ _min ]);
        }
            
        let _hh = Math.round(_min / 60);
        let _mm = Math.round(_min % 60);

        return this.lang.tr('@created_time_hours', [ _hh, _mm]);        
    }

    RequestElapsed(request){
        let _min = Math.round(((new Date()).getTime() - request.created.getTime()) / 60000);

        if (_min < 1){
            return this.lang.tr('@elapsed_time_just_now');            
        }
        
        if (_min < 60){
            return this.lang.tr('@elapsed_time_minutes', [ _min ]);
        }
            
        let _hh = Math.round(_min / 60);
        let _mm = Math.round(_min % 60);

        return this.lang.tr('@elapsed_time_hours', [ _hh, _mm]);
    }

    private _qrcodeForm = null;   
    get QrCodeForm(){
        if (this._qrcodeForm == null){
            this.LoadQrCodeForm();
        }
        return this._qrcodeForm;
    }

    get PlaceAreas(){
        let _values = [];
        for(let _area of this._place.areas){
            if (_area.IsValid){
                _values.push({
                    value: _area, text: _area.name
                })    
            }
        }

        // sort areas by name
        _values = _values.sort((a, b) => {
            return (a.text > b.text) ? 1 : -1;
        })

        _values.unshift({ 
            value: null, 
            text: this.tr('@place_table_display_noarea') 
        });

        return _values;
    }

    get PlaceTills(){
        let _values = [];
        for(let _possvc of this._place.possvc){
            if (_possvc.IsValid){
                let _posname = _possvc.name ? _possvc.name : this.lang.tr('@place_till_noname') + " (" + _possvc.name + ")";
                _values.push({
                    value: _possvc, text: _posname
                })    
            }
        }

        // sort services by name
        _values = _values.sort((a, b) => {
            return (a.text > b.text) ? 1 : -1;
        })

        return _values;
    }

    private _getSelectedArea(value){
        let _text = null;

        for(let _option of this.PlaceAreas){
            if (_option.value == value){
                _text = _option.text;
            }
        }

        return { value: value, text: _text }; 
    }

    private _getSelectedTill(value){
        if (!value || !value.IsValid){
            return { value: null, text: null };
        }

        let _text = null;

        for(let _option of this.PlaceTills){
            if (_option.value == value){
                _text = _option.text;
            }
        }

        return { value: value, text: _text }; 
    }

    private LoadQrCodeForm(){
        let _prefix = this._table.prefix;
        let _alias = this._table.alias;
        let _area = this._getSelectedArea(this._table.area);
        let _till = this._getSelectedTill(this._table.till);

        if (!this._qrcodeForm){
            this._qrcodeForm = new FormGroup({
                prefix: new FormControl(_prefix, [
                    // no validators required
                ]),
                alias: new FormControl(_alias, [
                    // opcional: si está vacío se usa el número. Máx. 45 (VARCHAR(45)).
                    Validators.maxLength(45)
                ]),
                area: new FormControl(_area, [
                    // no validators required
                ]),
                till: new FormControl(_till, [
                    Validators.required
                ])
            });
        }
        else {
            this._qrcodeForm.patchValue({
                prefix: _prefix,
                alias: _alias,
                area: _area,
                till: _till,
            });
        }
    
        this._OnRequiredFields();

        this._qrcodeForm.markAsPristine();
        setTimeout(() => {
            this._qrcodeForm.markAsPristine();
        }, 0);        
    }

    private _OnRequiredFields(){
        let _control = this.QrCodeForm.get('till');

        if (this.ChooseTill == false){
            _control.clearValidators();
            _control.setErrors(null);
            _control.updateValueAndValidity();
        }
    }

    private get _tableURL(){
        let _url = '';

        _url += AppConstants.thisURL + "qr-access?qr=true";
        if (AppConstants.NeedsSandboxAuth){    // taxes=='sandbox' del backend (fallback: build flag)
            _url += "&auth=controldeacceso";
        }
        _url += "&lang=" + this.lang.GetLanguage(true);
        _url += "&validQR=" + this._table.authorization;
        _url += "&table=" + this._table.objid;

        return _url;
    }

    private _tableQR = null;
    get TableQR(){
        // Regenerar si la URL cambio ademas de en el primer acceso: en build
        // --prod + backend sandbox, si esta vista se abre ANTES de que
        // version.php estampe serverTaxes, NeedsSandboxAuth cae al fallback
        // (!isProduction = false) y el QR cacheado quedaria sin auth para
        // toda la vida del componente. Comparar contra _tableURL lo corrige
        // en el siguiente ciclo de CD tras la respuesta de version.php.
        let _url = this._tableURL;
        if (!this._tableQR || (this._tableQR._qr_element_value != _url)){
            this._tableQR = {
                _qr_element_type: NgxQrcodeElementTypes.CANVAS,
                _qr_correction_level: NgxQrcodeErrorCorrectionLevels.HIGH,
                _qr_element_value: _url
            }
        }

        return this._tableQR;
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private sync: syncService, private data: dataService, public view: viewService, private modalCtrl: ModalController, private devsvc: deviceService) {
        super(lang, change);
    }

    private _close_subscription = null;
    private _elapsed_subscription = null;
    private _theme_subscription = null;

    private _CanClose(){
        return new Promise(async (resolve) => {
            let _form = this.QrCodeForm;
            if (_form.dirty){
                console.log("[EDIT TABLE] confirm changes lost");
                const alert = this.alertCtrl.alert({
                    header: this.lang.tr('@changes_lost_confirm_tittle'),
                    message: this.lang.tr('@changes_lost_confirm_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(true);
                            }, 100);
    
                            return false;
                        }
                    }, {
                        text: this.lang.tr('@no'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(false);
                            }, 100);
    
                            return false;
                        }
                    }]
                });
    
                alert.onDidDismiss().then((detail:any) => {
                    if (detail.data){
                        _form.markAsPristine();
                    }
                    
                    resolve(detail.data);
                });
    
                await alert.present();        
            }
            else {
                resolve(true);
            }
        });
    }

    OnViewChanged(){
        this.change.markForCheck();
    }

    ngOnInit(){
        this._close_subscription = this.view.OnCloseRequest.subscribe(
        async () => {
            this.view.OnCloseResponse(await this._CanClose());
        });        

        this._elapsed_subscription = this.data.Clock.OnOneMinuteTick.subscribe(
        data => {
           this.change.markForCheck();
        });

        this._theme_subscription = this.view.OnTheme.subscribe(
        data => {
            this.change.markForCheck();
        });
    }

    private _place_subscription = null;
    private _user_subscription = null;
    private _table_subscription = null;
    
    private async _OnCheckClose(){
        if (this._attention && !this._table.Attention){
            await this._OnClose();
        }
        this._attention = this._table.Attention;
    }

    private _refreshView(){
        this._OnCheckClose();

        this._pending = null;
        this._requests = null;
        this._brief = null;

        this.change.markForCheck();
    }

    ngOnChanges(){
        if (this._table_subscription){
            this._table_subscription.unsubscribe();
            this._table_subscription = null;
        }

        if (this._table) {
            this._table_subscription = this._table.OnRefresh.subscribe(
            data => {
                this._refreshView();
            });        
        }

        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }
        
        if (this._place) {
            this._place_subscription = this._place.OnTargetRefresh('QRCODE').subscribe(
            data => {
                this._refreshView();
            });
        }

        if (this._user_subscription){
            this._user_subscription.unsubscribe();
            this._user_subscription = null;
        }

        if (this._user) {
            this._user_subscription = this._user.OnRefresh.subscribe(
            data => {
                this.change.markForCheck();
            });
        }
            
        this._selected = null;
        this._refreshView();
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        if (this._elapsed_subscription){
            this._elapsed_subscription.unsubscribe();
            this._elapsed_subscription = null;
        }

        if (this._theme_subscription){
            this._theme_subscription.unsubscribe();
            this._theme_subscription = null;
        }

        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this._user_subscription){
            this._user_subscription.unsubscribe();
            this._user_subscription = null;
        }

        if (this._table_subscription){
            this._table_subscription.unsubscribe();
            this._table_subscription = null;
        }
    }

    @Output('onPanel') _onPanel = new EventEmitter<any>();     
    OnPanel(panel){
        this._onPanel.emit(panel);
    }

    private async _OnClose(){
        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        let _promise = new Promise((resolve) => {
            if (this.view.RightPanel){
                let _panel = {
                    scnd: null,
                    args: null
                };

                if (this._table.IsError){
                    _panel['main'] = 'view';
                }

                this._onPanel.emit(_panel);     
                resolve(true);
            }
            else {
                let _panel = {
                    scnd: null,
                };

                if (this._table.IsError){
                    _panel['main'] = 'view';
                }

                this._onPanel.emit(_panel);
    
                // wait for animation to finish
                setTimeout(() => {
                    this._onPanel.emit({
                        args: null
                    });    

                    resolve(true);
                }, 300);
            }    
        });

        await _promise;
    }

    async OnClose(){
        if (this.QrCodeForm.dirty){
            console.log("[EDIT TABLE] confirm changes lost");
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@changes_lost_confirm_tittle'),
                message: this.lang.tr('@changes_lost_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);

                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);

                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnClose();
                }
            });

            await alert.present();        
        }
        else {
            this._OnClose();
        }
    }

    async OnAccept(){
        if (!this._table.draw || !this._table.draw.IsValid){
            let _draw = new DrawItem(null, this.data);
            if (_draw){
                _draw.status = 'AC';
                _draw.type = 'TABLE';
                _draw.shape = 'SQUARE';
                _draw.coordinates = { x: 0, y: 0, w: 90, h: 90, r: 0 }                
            }
            
            this._table.draw = _draw;
        }

        this._table.prefix = this.QrCodeForm.get('prefix').value;
        this._table.alias = this.QrCodeForm.get('alias').value;
        this._table.area = this.QrCodeForm.get('area').value.value;
        this._table.till = this.QrCodeForm.get('till').value.value;

        this.QrCodeForm.markAsPristine();
        if (await this._table.DoCommit()){
            this._OnClose();
        }
    }

    @ViewChild('tableqr', { static: false }) tableqr: ElementRef;
    async OnDownload(){
        let filename = this._place.name + " - " + this._table.number + " - qrcode.png";

        if (this.tableqr){
            let canvas: HTMLCanvasElement = this.tableqr.nativeElement.querySelector('canvas');
            if (canvas) {
                let link = document.createElement("a");
                link.href = canvas.toDataURL('image/png');
                link.download = filename
    
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                let alert = this.alertCtrl.alert({
                    header: this.lang.tr('@place_download_tittle'),
                    message: this.lang.tr('@place_download_message', [ filename ]),
                    backdropDismiss: true,
                    buttons: [{
                        text: this.lang.tr('@ok'),
                        handler: (data) => {
                            setTimeout(() => {
                                alert.dismiss(false);
                            }, 100);
                            return false;
                        }
                    }]
                });
        
                await alert.present();        
            }    
        }
    }

    OnNewRequest(){
        let _ticket = this._place.CreateTicket(this._table);
        this._onPanel.emit({
            main: 'cart',
            scnd: null,
            args: {
                ticket: _ticket
            }
        });
    }

    private _OnTicketRefund(ticket){
        // update status for refund pending items
        for(let _ticketproduct of ticket.products){
            if (_ticketproduct.status == 'UN'){ 
                _ticketproduct.status = 'DE';    
            }
        }
        
        for(let _ticketoffer of ticket.offers){
            if (_ticketoffer.status == 'UN'){ 
                _ticketoffer.status = 'DE';    
            }
        }

        // delete the ticket if it has no products
        if (ticket.IsEmpty){  
            ticket.status = 'DE';
        }
    }

    /**
     * Actúa el cajón inteligente del ticket en la devolución de "camarero
     * atendida", cada método por SU vía (agnóstico al modelo del cajón):
     *
     *  - EFECTIVO ('PAYCASH'): dispensa el importe por `CASH_OUT`. Cualquier
     *    reciclador (CASHGUARD / GLORY / CASHLOGY / PROSEGUR) dispensa un
     *    importe sin referencia del cobro, así que funciona cross-device.
     *  - TARJETA ('PAYCARD'): abre el abono por el DATÁFONO. Solo PROSEGUR: el
     *    cross-device se resuelve en el backend por el `_uuid` del ticket (map
     *    del print_service). CASHLOGY necesitaría su transactionNumber LOCAL
     *    (no lo tenemos aquí, cobró otro dispositivo) y GLORY/CASHGUARD no
     *    llevan datáfono, así que en esos casos la tarjeta se da a mano.
     *
     * Sin cajón inteligente no hace nada: el camarero da la devolución a mano
     * (comportamiento de siempre). Best-effort: si falla/cancela, el operario
     * lo ve y lo gestiona; el flujo de "atendida" sigue.
     */
    private async _actuateSmartDrawerRefund(ticket): Promise<void> {
        let _cgInfo = this._place.smartdrawerForTicket(ticket);
        if (!_cgInfo || !_cgInfo.drawer){
            return;   // sin cajón inteligente -> a mano (como antes)
        }
        let _amount = Number(ticket.ToRefund) || 0;
        if (_amount <= 0){
            return;
        }
        // El ticket guarda el método en `payment` ('PAYCARD'/'PAYCASH'), lo mismo
        // que la vista usa para "Total en efectivo" vs "Total con tarjeta".
        if (ticket.payment == 'PAYCASH'){
            await this._smartDrawerCashOut(_cgInfo, _amount);
            return;
        }
        if (ticket.payment != 'PAYCARD'){
            return;   // otros métodos (bizum, etc.) -> a mano
        }
        // TARJETA: solo por el datáfono PROSEGUR (único cross-device por _uuid).
        if (_cgInfo.drawer.model != 'PROSEGUR'){
            return;   // sin datáfono cross-device -> a mano (como antes)
        }
        try {
            let _props: any = {
                tickets: [ ticket ],
                device: _cgInfo.drawer,
                place: _cgInfo.till,
                tolocal: _cgInfo.tolocal ?? false,
                cashless: true,
                refund: true,
                amountDue: _amount,
                // Cross-device: el backend resuelve el orderId del cobro por el
                // _uuid del ticket (map en el print_service).
                refundUuid: (ticket as any)._uuid,
            };
            let modal = await this.modalCtrl.create({
                component: UiCashGuardCashPayModal,
                cssClass: 'modal-cashguard',
                backdropDismiss: false,
                componentProps: _props,
            });
            await modal.present();
            await modal.onDidDismiss();
        } catch(e){
            console.error('[TABLE-REFUND] fallo al abrir el abono por datáfono', e);
        }
    }

    /**
     * Dispensa `amount` € por `CASH_OUT` del cajón del `cgInfo` (mismo patrón que
     * `_cashGuardCashOut` de la vista de ticket). Agnóstico al modelo: el target
     * del WS es `drawer.model`, y el print_service enruta al driver que dispensa
     * por importe. Best-effort: devuelve true solo si la máquina confirma SUCCESS.
     */
    private async _smartDrawerCashOut(cgInfo, amount: number): Promise<boolean> {
        try {
            let _till = cgInfo.till;
            if (!_till){
                return false;
            }
            let _tolocal = cgInfo.tolocal ?? false;
            let _host = _till.wshost;
            let _port = _till.wsport ? parseInt(_till.wsport, 10) : 5005;
            let _connection = await this.devsvc.Connect(_host, _port, _till.name, _tolocal);
            if (!_connection){
                return false;
            }
            let _target = cgInfo.drawer?.model || 'CASHGUARD';
            try {
                let _status = await _connection.Send(_target, { command: 'STATUS' });
                if (!(_status && _status.exec == 'SUCCESS')){
                    return false;
                }
                let _txId = Math.floor(Math.random() * 1000000);
                let _cents = Math.round(amount * 100);
                let _resp = await _connection.Send(_target, {
                    command: 'CASH_OUT',
                    transaction_id: _txId,
                    amount_due: _cents
                });
                return !!(_resp && _resp.exec == 'SUCCESS');
            } finally {
                this.devsvc.Disconnect(_connection);
            }
        } catch(e){
            console.error('[TABLE-REFUND] cashout error', e);
            return false;
        }
    }

    // Anti doble-click del "hecho" de la mesa: _OnRequestDone dispensa por el cajón
    // (CASH_OUT de la devolución de un pedido RECHAZADO; en Prosegur tarda) y luego
    // committea. Sin guard, un 2º toque reentraba y mandaba OTRA devolución. Se
    // bloquea al entrar (la vista es OnPush: markForCheck para que el botón se
    // pinte deshabilitado) y se rehabilita SIEMPRE al terminar.
    public _refunding = false;
    private async _OnRequestDone(){
        if (this._refunding){
            return;
        }
        this._refunding = true;
        this.change.markForCheck();
        try {
            await this._OnRequestDoneUnguarded();
        }
        finally {
            this._refunding = false;
            this.change.markForCheck();
        }
    }

    private async _OnRequestDoneUnguarded(){
        // Devolución por el datáfono del cajón inteligente ANTES de marcar (solo
        // si lo hay; si no, se marca y el camarero da a mano, como hasta ahora).
        for(let _request of this.Requests){
            if (_request.reason == 'REFUND'){
                await this._actuateSmartDrawerRefund(_request.ticket);
            }
        }

        let _transaction = await this.data.BeginTransaction();
        if (_transaction){
            for(let _request of this.Requests){
                if (_request.reason == 'REFUND'){
                    this._OnTicketRefund(_request.ticket);
                    _request.ticket.DoUpdate();
                    _transaction.SendCommit(_request.ticket);
                }

                if (_request.reason == 'PAYCARD'){
                    _request.ticket.payment = 'PAYCARD';
                    _request.ticket.DoUpdate();
                    _transaction.SendCommit(_request.ticket);
                }

                if (_request.reason == 'PAYCASH'){
                    _request.ticket.payment = 'PAYCASH';
                    _request.ticket.DoUpdate();
                    _transaction.SendCommit(_request.ticket);
                }

                _request.status = 'DE';
                _transaction.SendCommit(_request);
            }

            await _transaction.Flush(false);
        }

        await this._OnClose();
    }

    async RequestDone(){
        // Guard desde el PRIMER toque (Copilot #1811): la confirmación es un alert
        // asíncrono; si el guard se tomara solo al confirmar, un doble toque abría
        // DOS alerts y el segundo podía confirmarse cuando el primer CASH_OUT ya
        // había terminado (y _refunding vuelto a false) -> segunda devolución.
        // Se toma aquí, se mantiene durante confirmación + ejecución y se suelta
        // SIEMPRE (también al cancelar o cerrar el alert por el fondo).
        if (this._refunding){
            return;
        }
        if (this.Brief.amount != 0){
            this._refunding = true;
            this.change.markForCheck();

            let _message = null;
            if (this.Brief.torefund){
                _message = this.lang.tr('@place_table_request_confirm_refund', [ this.Brief.amount.toFixed(2), this.lang.tr('@currency') ]);
            }
            else {
                _message = this.lang.tr('@place_table_request_confirm_income', [ this.Brief.amount.toFixed(2), this.lang.tr('@currency') ]);
            }

            let alert = this.alertCtrl.alert({
                header: this.lang.tr('@place_table_request_confirm'),
                message: _message,
                backdropDismiss: true,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: (data) => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: (data) => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then(async (detail:any) => {
                try {
                    if (detail && detail.data){
                        await this._OnRequestDoneUnguarded();
                    }
                }
                finally {
                    this._refunding = false;
                    this.change.markForCheck();
                }
            });

            try {
                await alert.present();
            }
            catch(e){
                // Si el alert no llega a presentarse no habrá onDidDismiss: soltar.
                console.error('[TABLE] alert de confirmación falló', e);
                this._refunding = false;
                this.change.markForCheck();
            }
        }
        else {
            this._OnRequestDone();
        }
    }

    /***********************************/
    /* TICKET VIEW                     */
    /***********************************/

    public _selected: Ticket = null;
    SelectTicket(_ticket){  
        if (this._selected != _ticket){
            this._selected = _ticket;
            
            if (_ticket == null){
                this.change.markForCheck();
            }    
        }

        this.change.markForCheck();
    }

    OnShowTicket(ticket){
        if (ticket){
            this.SelectTicket(ticket);
        }
    }
}
